import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { EllipsisVertical, SquarePen, Trash2 } from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { Menu, MenuItem, MenuTrigger } from '@repo/components/menu';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import {
  queryAllOrganizationServiceUsers,
  queryAllOrganizationUsers,
  queryOrganizationFeatures,
  queryOrganizationRoles,
} from '~/services/authorization/authorization.ts';
import type { Role } from '~/services/authorization/types.ts';

import { AlertDeleteRole } from './-components/alert-delete-role.tsx';
import { DialogCreateRole } from './-components/dialog-create-role.tsx';
import { DialogEditRole } from './-components/dialog-edit-role.tsx';
import { groupMembersByRole, RoleCardStats } from './-components/role-card-stats.tsx';

export const Route = createFileRoute('/(user-mgmt)/organization/$org/user-mgmt/roles')({
  loader: async ({ context: { queryClient }, params: { org } }) => {
    await Promise.all([
      queryClient.ensureQueryData(queryOrganizationRoles(org)),
      queryClient.ensureQueryData(queryOrganizationFeatures(org)),
      queryClient.ensureQueryData(queryAllOrganizationUsers(org)),
      queryClient.ensureQueryData(queryAllOrganizationServiceUsers(org)),
    ]);
  },
  component: RouteComponent,
});

type Dialog = { type: 'delete' | 'edit'; role: Role };

function RouteComponent() {
  const orgId = Route.useParams().org;
  const roles = useSuspenseQuery(queryOrganizationRoles(orgId)).data;
  const features = useSuspenseQuery(queryOrganizationFeatures(orgId)).data;
  const persons = useSuspenseQuery(queryAllOrganizationUsers(orgId)).data;
  const services = useSuspenseQuery(queryAllOrganizationServiceUsers(orgId)).data;

  const [dialog, setDialog] = useState<Dialog>();
  const [search, setSearch] = useState('');

  const membersByRole = useMemo(() => groupMembersByRole(persons, services), [persons, services]);

  const filteredRoles = useMemo(() => {
    const query = search.toLowerCase().trim();

    return roles.filter(role => role.name
      .concat(role.description)
      .toLowerCase()
      .trim()
      .includes(query));
  }, [roles, search]);

  return (
    <>
      {dialog?.type === 'delete' && (
        <AlertDeleteRole
          close={() => {
            setDialog(undefined);
          }}
          role={dialog.role}
          parentId={orgId}
          type="organization"
        />
      )}
      {dialog?.type === 'edit' && (
        <DialogEditRole
          close={() => {
            setDialog(undefined);
          }}
          role={dialog.role}
          features={features}
          parentId={orgId}
          type="organization"
        />
      )}

      <div className="flex gap-4 flex-wrap">
        <SearchField
          isDisabled={!roles.length}
          name="search"
          autoComplete="off"
          aria-label="Search roles"
          placeholder="Search roles"
          value={search}
          onChange={(val) => {
            setSearch(val);
          }}
        />
        <FeatureGuard features={{ key: 'user-management:manage-roles', scope: 'Organization' }}>
          <DialogCreateRole
            type="organization"
            parentId={orgId}
            features={features}
          />
        </FeatureGuard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 justify-start w-full mt-4">
        {!roles.length
          ? (
              <p className="mt-4">
                No roles have been created.
              </p>
            )
          : !filteredRoles.length && (
              <div className="flex flex-col gap-4 items-start mt-4">
                <p>No roles found matching your search.</p>
                <Button variant="soft" action={() => { setSearch(''); }}>Clear search</Button>
              </div>
            )}
        {filteredRoles.map(role => (
          <Card key={role.id} className="flex flex-col justify-between gap-4">
            <div className="flex justify-between items-start gap-6">
              <div className="flex flex-col gap-1">
                <p className="font-medium">{role.name}</p>
                <p className="text-sm">{role.description}</p>
              </div>
              {role.type === 'Custom' && (
                <FeatureGuard features={{ key: 'user-management:manage-roles', scope: 'Organization' }}>
                  <MenuTrigger>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      color="secondary"
                      icon={{ component: <EllipsisVertical strokeWidth={1.5} aria-label="Open role actions" /> }}
                    />
                    <Popover>
                      <Menu>
                        <MenuItem onAction={() => { setDialog({ type: 'edit', role }); }}>
                          <SquarePen size={18} />
                          Edit
                        </MenuItem>
                        <MenuItem onAction={() => { setDialog({ type: 'delete', role }); }}>
                          <Trash2 size={18} />
                          Delete
                        </MenuItem>
                      </Menu>
                    </Popover>
                  </MenuTrigger>
                </FeatureGuard>
              )}
            </div>

            <RoleCardStats
              members={membersByRole.get(role.id) ?? []}
              permissions={role.permissions}
            />
          </Card>
        ))}
      </div>
    </>
  );
}
